import type { RegressionAlert } from '../types/gainsMonitor';

/**
 * regression_alerts é um log de detecções.
 * Várias linhas não resolvidas do mesmo Kaizen e da mesma métrica
 * continuam no histórico. A leitura principal projeta uma condição atual:
 * o evento não resolvido mais recente daquele par.
 *
 * Status regredido e alerta aberto são conceitos distintos.
 * Um Kaizen pode estar regredido sem condição aberta, e uma condição
 * aberta pode existir sem o status regredido.
 */

const SEVERITY_RANK: Record<RegressionAlert['severity'], number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

function eventTime(alert: RegressionAlert): number {
  const value = new Date(alert.detected_at).getTime();
  return Number.isFinite(value) ? value : 0;
}

export function currentAlertConditions(events: readonly RegressionAlert[]): RegressionAlert[] {
  const latest = new Map<string, RegressionAlert>();
  for (const event of events) {
    if (event.resolved) continue;
    const key = `${event.kaizen_id}\0${event.metric_name}`;
    const current = latest.get(key);
    if (!current) {
      latest.set(key, event);
      continue;
    }
    const newer = eventTime(event) > eventTime(current)
      || (eventTime(event) === eventTime(current) && String(event.created_at ?? '') > String(current.created_at ?? ''));
    if (newer) latest.set(key, event);
  }
  return [...latest.values()].sort((a, b) => {
    const bySeverity = (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3);
    if (bySeverity !== 0) return bySeverity;
    return eventTime(b) - eventTime(a);
  });
}

export type OpenAlertsRead =
  | { status: 'ok'; events: RegressionAlert[]; conditions: RegressionAlert[] }
  | { status: 'error' };

export function openAlertsHeadline(read: OpenAlertsRead): string {
  if (read.status === 'error') return 'Condições atuais';
  return `Condições atuais (${read.conditions.length})`;
}

export function openAlertsDetail(read: OpenAlertsRead): string | null {
  if (read.status === 'error') return 'Não foi possível carregar os alertas.';
  if (read.events.length > read.conditions.length) {
    return `O histórico conserva ${read.events.length} eventos em aberto. A lista mostra a condição mais recente de cada métrica.`;
  }
  if (read.conditions.length === 0) return 'Nenhuma condição em aberto.';
  return null;
}
